import dayjs from "../utils/dayjs";


//// ----------- HELPERS ----------- ////

export const groupBy = (arr, key) => {
  if (!arr) return {};
  const fn = typeof key === "function" ? key : (item) => item[key];
  return arr.reduce((acc, item) => {
    const group = fn(item);
    (acc[group] = acc[group] || []).push(item);
    return acc;
  }, {});
};

export const keyBy = (arr, key) => {
  if (!arr) return {};
  return arr.reduce((acc, item) => {
    acc[item[key]] = item;
    return acc;
  }, {});
};

//// ----------- FORMATTING ----------- ////

// Pass number and decimal count to format the number. If the number is an integer, it will return the number as is.
export function formatNumber(num, decimalCount) {
  if (!num) return num;
  const isInt = num % 1 === 0;
  if (isInt) return num;
  const fixedNum = num.toFixed(decimalCount);
  //parsing to remove trailing zeros
  const parsedNum = parseFloat(fixedNum);
  return parsedNum;

}

export function formatDate(date) {
  const d = new Date(date);
  return d.toLocaleDateString();
}

// Matches the dd.mm.yyyy [HH:mm[:ss]] strings ControlApp sends (e.g. "17.09.2026 07:46:32").
// The JS Date constructor can't parse dot-separated dates, so these need to be parsed manually.
const EUROPEAN_DATE_REGEX = /^(\d{1,2})\.(\d{1,2})\.(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/;

// Always formats as dd.mm.yyyy HH:mm (European style), regardless of the browser's locale or
// whether the input is already dd.mm.yyyy (ControlApp) or an ISO-ish string (DataManager/Python).
export function formatDateTime(date) {
  if (!date) return "";

  const match = typeof date === "string" && date.match(EUROPEAN_DATE_REGEX);
  const d = match
    ? new Date(match[3], match[2] - 1, match[1], match[4] || 0, match[5] || 0, match[6] || 0)
    : new Date(date);

  if (isNaN(d.getTime())) return String(date); // unparseable, show raw value instead of NaNs

  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${day}.${month}.${year} ${hours}:${minutes}`;
}

// Extracts just the HH:mm:ss portion, whether the input is "HH:mm:ss" alone or a full
// "dd.mm.yyyy HH:mm:ss" timestamp - handles both old and new StartTime payload shapes.
export function formatTimeOnly(value) {
  if (!value) return "";
  const parts = String(value).trim().split(' ');
  return parts[parts.length - 1];
}

export function formatDateUTC(date, includeTime = false) {
  const d = new Date(date);
  const day = String(d.getUTCDate()).padStart(2, '0');
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const year = d.getUTCFullYear();

  if (includeTime) {
    const hours = String(d.getUTCHours()).padStart(2, '0');
    const minutes = String(d.getUTCMinutes()).padStart(2, '0');
    return `${day}.${month}.${year} ${hours}:${minutes} (UTC)`;
  } else {
    return `${day}.${month}.${year} (UTC)`;
  }

}

export const dateStringDayAndMonthSwapPlaces = (string) => {  // example incoming string 03/30/2022 12:21:31
  if (!string) return "";
  const [datePart, timePart] = string.split(' ');
  if (!datePart.includes('/')) return string; // already dd.mm.yyyy (dot separated), nothing to swap
  const [month, day, year] = datePart.split('/');
  return `${day}.${month}.${year}${timePart ? ' ' + timePart : ''}`;
};

export const WaitingTime = 1000 * 60 * 5;

export const getWindowHeight = () => {
  const windowHeight =
    window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
  return windowHeight;
};


export const upperCaseFirstLetter = (str) => str.charAt(0).toUpperCase() + str.slice(1);

export const getDateRange = (type) => {
  const format = "DD-MM-YYYY HH:mm:ss";
  // Replace 2x new Date() below with one common Date()?
  let now = dayjs().format(format);
  const lastHour = dayjs().subtract(1, "hour").format(format);
  let dateDomain = {
    start: lastHour,
    end: now,
  };
  switch (type) {
    case "Hour": {
      dateDomain = {
        start: lastHour,
        end: now,
      };
      break;
    }

    case "Day": {
      now = dayjs().format(format);
      const lastDay = dayjs().subtract(1, "day").format(format);
      dateDomain = {
        start: lastDay,
        end: now,
      };
      break;
    }

    case "Week": {
      now = dayjs().format(format);
      const lastWeek = dayjs().subtract(1, "week").format(format);
      dateDomain = {
        start: lastWeek,
        end: now,
      };
      break;
    }

    case "Month": {
      now = dayjs().format(format);
      const lastMonth = dayjs().subtract(1, "month").format(format);
      dateDomain = {
        start: lastMonth,
        end: now,
      };
      break;
    }

    case "Year": {
      now = dayjs().format(format);
      const lastYear = dayjs().subtract(1, "year").format(format);
      dateDomain = {
        start: lastYear,
        end: now,
      };
      break;
    }
    default:
      break;
  }
  return dateDomain;
};

// Helper function to format timestamps from LoRa devices.
const formatLoraTimestamp = (timestamp) => {
  if (!timestamp) return "";

  const date = new Date(timestamp);
  const formattedDate = [
    date.getDate(), date.getMonth() + 1, date.getFullYear()
  ].map(part => part.toString().padStart(2, '0')).join('.');
  const formattedTime = [
    date.getHours(), date.getMinutes()
  ].map(part => part.toString().padStart(2, '0')).join(':');

  return `${formattedDate} ${formattedTime}`;
};


/**
 * Merge incoming socket messages with previous formatted entries and config definitions.
 * Only processes hwIds that received messages in this batch; others are preserved as-is.
 *
 * @param {Array} data - Raw socket messages (array of {HwId, ValueName, Value, ...})
 * @param {Object} previousEntries - Previous formattedEntries (hwId → valueName → formatted msg)
 * @param {Object} configs - Device/sample configs (hwId → valueName → config definition)
 * @returns {Object} Updated formattedEntries
 */
export const mergeFormattedEntries = (data, previousEntries, configs) => {
  const devicesGroups = groupBy(data, "HwId");
  const result = { ...previousEntries };

  Object.keys(devicesGroups).forEach((hwId) => {
    const hwConfigs = configs[hwId];
    if (!hwConfigs) return;

    const messages = keyBy(devicesGroups[hwId], "ValueName");
    const lastFormatted = previousEntries[hwId] || {};
    const formattedMessages = {};

    Object.entries(messages).forEach(([messageKey, messageValue]) => {
      const config = hwConfigs[messageKey];
      if (!config) return;

      const value = parseFloat(messageValue.Value) || 0;
      const formattedValue = config.floatPrecision ? value.toFixed(config.floatPrecision) : value.toFixed(0);

      formattedMessages[messageKey] = {
        displayName: config.displayName,
        valueName: messageValue.ValueName,
        valueTimestamp: formatLoraTimestamp(messageValue.ValueTimestamp),
        tag: config.tag,
        value: messageValue.ValueString || formattedValue,
        hasParameters: !!messageValue.hasParameters,
        type: messageValue.Type,
        address: messageValue.Address || "",
        unit: messageValue.Unit || "",
        hwId: messageValue.HwId,
        flag: config.flag,
        icon: config.flag === "command" ? config.icon : undefined,
        toggle: config.flag === "command" ? config.toggle : undefined,
        press: config.flag === "command" ? config.press : undefined,
        release: config.flag === "command" ? config.release : undefined,
        customerId: messageValue.CustomerId,
        deviceId: messageValue.DeviceId,
      };
    });

    result[hwId] = { ...hwConfigs, ...lastFormatted, ...formattedMessages };
  });

  return result;
};

// Every device is expected to report a "Communication" diagnostic. If a device's config doesn't
// list it (e.g. it was left out of the DeviceCards config), add a default entry so it can still be
// picked/displayed instead of being silently unrecognized.
const DEFAULT_COMMUNICATION_DIAGNOSTIC = { name: "Communication", valueName: "Communication", floatPrecision: 0 };

export const ensureCommunicationDiagnostic = (devices) => {
  if (!Array.isArray(devices)) return devices;

  return devices.map((device) => {
    const diagnostics = device.diagnostics || [];
    const hasCommunication = diagnostics.some((item) => item.valueName === "Communication");
    if (hasCommunication) return device;

    return { ...device, diagnostics: [...diagnostics, { ...DEFAULT_COMMUNICATION_DIAGNOSTIC }] };
  });
};


export const formattedDevicesConfigs = (data) => {
  const devicesItems = keyBy(data, "hwId");
  let devicesConfigs = {};

  // Utility function to create config items
  const createConfigItems = (items, key, flag) => {
    let configItems = {};
    items.forEach(item => {
      const id = item.valueName || item.name;
      configItems[id] = {
        displayName: item.name,
        id: id,
        floatPrecision: item.floatPrecision,
        value: "-",
        flag: flag,
        hwId: key,
        icon: item.icon,
        toggle: item.toggle,
        press: item.press,
        release: item.release,
        tag: item.tag,
        group: item.group || "",
      };
    });
    return configItems;
  };

  Object.keys(devicesItems).forEach(key => {
    const device = devicesItems[key];
    // Creation of diagnostics, measurements, and commands using the utility function
    const diagnostics = createConfigItems(device.diagnostics || [], key, "diagnostic");
    const measurements = createConfigItems(device.measurements || [], key, "measurement");
    const commands = createConfigItems(device.commands || [], key, "command");

    // Merge the created configurations into the devicesConfigs object
    devicesConfigs[key] = {
      ...diagnostics,
      ...measurements,
      ...commands,
      gps: { ...device.gps, flag: "gps" },
    };
  });

  return devicesConfigs;
};



export const formattedSamplesConfigs = (data) => {
  const samplesItems = keyBy(data, "hwId");
  let samplesConfigs = {};

  for (const key in samplesItems) {
    const diagnosticsList = keyBy(samplesItems[key].diagnostics, "valueName");
    const measurementList = keyBy(samplesItems[key].measurements, "valueName");
    const analyzersList = keyBy(samplesItems[key].analyzers, "valueName") || {};

    const diagnostics = {};
    const measurements = {};
    const analyzers = {};

    for (const dKey in diagnosticsList) {
      diagnostics[dKey] = {
        displayName: diagnosticsList[dKey].name,
        id: diagnosticsList[dKey].valueName,
        floatPrecision: diagnosticsList[dKey].floatPrecision,
        value: parseFloat("0").toFixed(diagnosticsList[dKey].floatPrecision),
        flag: "diagnostic",
        hwId: key,
      };
    }
    for (const mKey in measurementList) {
      measurements[mKey] = {
        displayName: measurementList[mKey].name,
        id: measurementList[mKey].valueName,
        tag: measurementList[mKey].tag,
        floatPrecision: measurementList[mKey].floatPrecision,
        value: parseFloat("0").toFixed(measurementList[mKey].floatPrecision),
        flag: "measurement",
        hwId: key,
      };
    }
    for (const deviceKey in analyzersList) {
      analyzers[deviceKey] = {
        displayName: analyzersList[deviceKey].name,
        id: analyzersList[deviceKey].valueName/*  + "-analyzer" */,
        analyzerDiagnostics: analyzersList[deviceKey].analyzerDiagnostics,
        // tag: analyzersList[deviceKey].tag,
        floatPrecision: analyzersList[deviceKey].floatPrecision,
        value: parseFloat("0").toFixed(analyzersList[deviceKey].floatPrecision),
        flag: "analyzer",
        hwId: key,
      };
    }

    samplesConfigs[key] = {
      ...diagnostics,
      ...measurements,
      ...analyzers,
    };
  }

  return samplesConfigs;
};

export const filterColor = (diagnostics, measurements, extraDiagnostics) => {
  let color = "green";
  const arrayValues = [...diagnostics, ...measurements, ...extraDiagnostics];
  const findRed = arrayValues.find((o) => {
    return (o.category === "SampleCondition" || o.category === "Device") && o.isActive === true;
  });
  if (findRed) {
    color = "red";
    return color;
  }
  const findOrange = arrayValues.find((o) => {
    return o.category === "Warning" && o.isActive === true;
  });
  if (findOrange) {
    color = "orange";
    return color;
  }
  return color;
};

export const generateDeviceListForUI = (
  data,
  isDefault,
  uiState,
  alarmsMessages,
  extraDiagnostics
) => {
  if (!data || Object.keys(data).length === 0) { return []; }

  const devicesList = [];
  const diagnosticsPickerList = uiState.overview?.diagnosticsPickerList || [];

  Object.keys(data).forEach((hwId) => {
    const deviceData = data[hwId];
    const deviceAlarms = alarmsMessages?.[hwId] || [];
    const alarmsGroupByName = groupBy(deviceAlarms, "displayName");

    // Initialize arrays for different types of data
    const diagnostics = [], measurements = [], commands = [], extraDiag = [], diagnosticsPickers = [];

    // Handle extra diagnostics
    processExtraDiagnostics(extraDiagnostics?.[hwId], alarmsGroupByName, isDefault, extraDiag);

    // Process each message in the device data
    Object.keys(deviceData).forEach((messageKey) => {
      const message = deviceData[messageKey];
      const { flag, displayName, value, unit, valueName } = message;
      const lastAlarm = alarmsGroupByName[valueName]?.[0];

      if (flag === "diagnostic") {
        processDiagnostics(diagnosticsPickerList, hwId, displayName, value, unit, isDefault, lastAlarm, diagnostics, diagnosticsPickers, message);
      } else if (flag === "measurement") {
        measurements.push(createDataEntry(message, isDefault, lastAlarm));
      } else if (flag === "command") {
        commands.push(message); // Commands remain unchanged
      }
      // Additional flags can be handled here
    });

    const deviceBgColor = filterColor(diagnostics, measurements, extraDiag);

    devicesList.push({
      hwId,
      diagnostics,
      measurements,
      extraDiagnostics: extraDiag,
      commands,
      deviceBgColor,
      diagnosticsPickers,
    });
  });

  return devicesList;
};

function processExtraDiagnostics(extraDiagnostics, alarmsGroupByName, isDefault, extraDiag) {
  Object.keys(extraDiagnostics || {}).forEach((key) => {
    const lastAlarm = alarmsGroupByName[key]?.[0];
    extraDiag.push({
      ...extraDiagnostics[key],
      category: getCategory(isDefault, lastAlarm),
    });
  });
}

function processDiagnostics(diagnosticsPickerList, hwId, displayName, value, unit, isDefault, lastAlarm, diagnostics, diagnosticsPickers, message) {
  const diagnosticItemList = diagnosticsPickerList.filter(item => item.hwId === hwId && item.displayName === displayName);
  if (diagnosticItemList.length > 0) {
    diagnosticsPickers.push({
      ...diagnosticItemList[0],
      value,
      unit,
      category: getCategory(isDefault, lastAlarm),
      isActive: lastAlarm ? lastAlarm.isActive : false,
    });
  }
  diagnostics.push(createDataEntry(message, isDefault, lastAlarm));
}

function createDataEntry(message, isDefault, lastAlarm) {
  return {
    ...message,
    category: getCategory(isDefault, lastAlarm),
    isActive: lastAlarm ? lastAlarm.isActive : false,
  };
}

function getCategory(isDefault, lastAlarm) {
  return isDefault ? "Normal" : lastAlarm ? lastAlarm.category : "Normal";
}



export const generateSampleListForUI = (
  data,
  isDefault,
  uiState,
  alarmsMessages,
  extraDiagnostics
) => {
  const samplesList = [];

  if (data && Object.keys(data).length > 0) {
    for (const entry in data) {
      const alarmsForThisHwId = alarmsMessages && alarmsMessages[entry];
      const alarmsGroupByName = groupBy(alarmsForThisHwId, "displayName");
      const hwId = entry;
      const messages = data[entry];
      const diagnostics = [];
      const measurements = [];
      const analyzers = [];
      const extraDiag = [];

      const eds = extraDiagnostics && extraDiagnostics[entry];
      if (eds) {
        for (const ed in eds) {
          const alarms = alarmsGroupByName[ed];
          const lastAlarm = alarms && alarms.length > 0 && alarms[0];

          if (lastAlarm) {
            extraDiag.push({
              ...eds[ed],
              category: isDefault ? "Normal" : lastAlarm ? lastAlarm.category : "Normal",
            });
          }
        }
      }

      for (const message in messages) {
        const { flag, displayName } = messages[message];
        const alarms = alarmsGroupByName[message];
        const lastAlarm = alarms && alarms.length > 0 && alarms[0];

        if (flag === "diagnostic") {
          diagnostics.push({
            ...messages[message],
            category: isDefault ? "Normal" : lastAlarm ? lastAlarm.category : "Normal",
            isActive: lastAlarm ? lastAlarm.isActive : false,
          });
        } else if (flag === "measurement") {
          measurements.push({
            ...messages[message],
            category: isDefault ? "Normal" : lastAlarm ? lastAlarm.category : "Normal",
            isActive: lastAlarm ? lastAlarm.isActive : false,
          });
        } else if (flag === "analyzer") {
          // console.log("analyzer", {...messages[message]});
          analyzers.push({
            ...messages[message],
            category: isDefault ? "Normal" : lastAlarm ? lastAlarm.category : "Normal",
            isActive: lastAlarm ? lastAlarm.isActive : false,
          });
        }
      }

      const sampleBgColor = filterColor(diagnostics, measurements, extraDiag);
      const item = {
        hwId,
        diagnostics,
        measurements,
        analyzers,
        extraDiagnostics: extraDiag,
        sampleBgColor,
      };
      samplesList.push(item);
    }
  }

  return samplesList;
};

export default {};
